import { useState, useEffect } from "react";
import { FileText, FolderKanban, CreditCard, MessageSquare } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = ({ customer }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, invoicesRes, projectsRes] = await Promise.all([
        fetch(`${API}/customer/profile`, { credentials: "include" }),
        fetch(`${API}/customer/invoices`, { credentials: "include" }),
        fetch(`${API}/customer/projects`, { credentials: "include" })
      ]);

      if (profileRes.ok) setProfile(await profileRes.json());
      if (invoicesRes.ok) {
        const data = await invoicesRes.json();
        setInvoices(data?.invoices ?? data ?? []);
      }
      if (projectsRes.ok) {
        const data = await projectsRes.json();
        setProjects(data?.projects ?? data ?? []);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const unpaidInvoices = (invoices || []).filter(i => i.status !== "paid");
  const totalDue = (unpaidInvoices || []).reduce((sum, i) => sum + (i.total || 0), 0);
  const activeProjects = (projects || []).filter(p => p.status !== "completed");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-dashboard">
      {/* Welcome Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">
          Welcome, {customer?.name?.split(' ')[0]}
        </h1>
        <p className="text-[#A1A1AA] mt-1">Your DopeInHD client portal</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stat-card card-hover cursor-pointer" onClick={() => navigate("/client/invoices")}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A1A1AA]">Outstanding Balance</p>
                <p className={`text-2xl font-bold mt-1 ${totalDue > 0 ? 'text-[#F59E0B]' : 'text-[#22C55E]'}`}>
                  {formatCurrency(totalDue)}
                </p>
                <p className="text-xs text-[#71717A] mt-1">{unpaidInvoices.length} unpaid invoice(s)</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <CreditCard className="text-[#D4AF37]" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card card-hover cursor-pointer" onClick={() => navigate("/client/projects")}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A1A1AA]">Active Projects</p>
                <p className="text-2xl font-bold text-[#FAFAFA] mt-1">{activeProjects.length}</p>
                <p className="text-xs text-[#71717A] mt-1">In progress</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#3B82F6]/10 flex items-center justify-center">
                <FolderKanban className="text-[#3B82F6]" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card card-hover cursor-pointer" onClick={() => navigate("/client/documents")}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A1A1AA]">Documents</p>
                <p className="text-2xl font-bold text-[#FAFAFA] mt-1">View Files</p>
                <p className="text-xs text-[#71717A] mt-1">Contracts & uploads</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#22C55E]/10 flex items-center justify-center">
                <FileText className="text-[#22C55E]" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Invoices */}
      <Card className="bg-[#121214] border-[#27272A]">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-[#FAFAFA]">Recent Invoices</CardTitle>
          <Button variant="ghost" onClick={() => navigate("/client/invoices")} className="text-[#D4AF37]">
            View All
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(invoices || []).slice(0, 3).map((invoice) => (
              <div 
                key={invoice.id}
                className="flex items-center justify-between p-4 bg-[#1A1A1D] rounded-lg"
              >
                <div>
                  <p className="font-medium text-[#FAFAFA]">{invoice.invoice_number}</p>
                  <p className="text-sm text-[#71717A]">Due: {invoice.due_date}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-[#D4AF37]">{formatCurrency(invoice.total)}</p>
                  <span className={`badge ${
                    invoice.status === 'paid' ? 'badge-success' : 
                    invoice.status === 'overdue' ? 'badge-error' : 'badge-warning'
                  }`}>
                    {invoice.status}
                  </span>
                </div>
              </div>
            ))}
            {(invoices || []).length === 0 && (
              <p className="text-center text-[#71717A] py-4">No invoices yet</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Active Projects */}
      <Card className="bg-[#121214] border-[#27272A]">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-[#FAFAFA]">Your Projects</CardTitle>
          <Button variant="ghost" onClick={() => navigate("/client/projects")} className="text-[#D4AF37]">
            View All
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(projects || []).slice(0, 3).map((project) => (
              <div 
                key={project.id}
                className="p-4 bg-[#1A1A1D] rounded-lg"
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-[#FAFAFA]">{project.title}</p>
                  <span className={`badge ${
                    project.status === 'completed' ? 'badge-success' : 
                    project.status === 'active' ? 'badge-gold' : 'badge-warning'
                  }`}>
                    {project.status}
                  </span>
                </div>
                {project.client_visible_notes && (
                  <p className="text-sm text-[#A1A1AA]">{project.client_visible_notes}</p>
                )}
                {project.deadline && (
                  <p className="text-xs text-[#71717A] mt-2">Deadline: {project.deadline}</p>
                )}
              </div>
            ))}
            {(projects || []).length === 0 && (
              <p className="text-center text-[#71717A] py-4">No projects yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerDashboard;
